import { useEffect, useState } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { FileDown, Loader2 } from 'lucide-react';
import type { Nom35Assessment } from '@/types';

export interface HistoryEvent {
  id: string;
  period: string;
  date: string;
  type: 'NOM-035';
  score: number;
  assessmentId?: string;
  guideType?: string;
}

/** Convierte nivel de riesgo NOM-035 a score 0-100 (bienestar: mayor = mejor). */
function nom35LevelToScore(level: string | undefined, requiresClinicalAttention?: boolean): number {
  if (requiresClinicalAttention === true) return 25;
  const normalized = (level ?? '').toLowerCase().replace(/\s/g, '');
  if (normalized === 'nulo') return 95;
  if (normalized === 'bajo') return 85;
  if (normalized === 'medio') return 60;
  if (normalized === 'alto') return 35;
  if (normalized === 'muyalto') return 15;
  return 50;
}

/** Calcula score 0-100 para NOM-035. G1 usa requiresClinicalAttention; G2/G3 usan resultLevel. */
function getNom35Score(a: Nom35Assessment): number {
  if (a.guideType === 'NOM35_G1') {
    return a.requiresClinicalAttention === true ? 25 : 95;
  }
  return nom35LevelToScore(a.resultLevel);
}

function getTrendSummary(events: HistoryEvent[]): { label: string; emoji: string } {
  if (events.length < 2) return { label: 'Sin datos suficientes', emoji: '➖' };
  const scores = events.map((e) => e.score);
  const first = scores[0];
  const last = scores[scores.length - 1];
  const diff = last - first;
  if (diff > 5) return { label: 'Mejorando', emoji: '↗️' };
  if (diff < -5) return { label: 'En descenso', emoji: '↘️' };
  return { label: 'Estable', emoji: '➡️' };
}

function formatPeriod(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-MX', { month: 'short', year: 'numeric' });
}

export interface EmployeeHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  employeeName: string;
  companyId: string;
  /** Callback al hacer clic en "Ver Reporte" para abrir expediente del empleado. */
  onViewNom35Report?: (employeeId: string, employeeName: string) => void;
}

export function EmployeeHistoryDialog({
  open,
  onOpenChange,
  employeeId,
  employeeName,
  companyId,
  onViewNom35Report,
}: EmployeeHistoryDialogProps) {
  const [historyData, setHistoryData] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !companyId || !employeeId) {
      setHistoryData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const events: HistoryEvent[] = [];

        // NOM-035 completadas
        const nom35Query = query(
          collection(db, 'nom35_assessments'),
          where('companyId', '==', companyId),
          where('employeeId', '==', employeeId),
          where('status', '==', 'completed')
        );
        const nom35Snap = await getDocs(nom35Query);
        const nom35Docs = nom35Snap.docs
          .map((d) => ({ doc: d, data: d.data() as Nom35Assessment }))
          .filter(({ data }) => (data.completedAt ?? data.createdAt ?? '').length > 0)
          .sort((a, b) => {
            const dateA = a.data.completedAt ?? a.data.createdAt ?? '';
            const dateB = b.data.completedAt ?? b.data.createdAt ?? '';
            return dateB.localeCompare(dateA);
          });
        nom35Docs.forEach(({ doc: d, data }) => {
          const completedAt = data.completedAt ?? data.createdAt ?? '';
          if (!cancelled) {
            events.push({
              id: d.id,
              period: formatPeriod(completedAt),
              date: completedAt,
              type: 'NOM-035',
              score: getNom35Score({ ...data, id: d.id }),
              assessmentId: d.id,
              guideType: data.guideType,
            });
          }
        });

        // Solo NOM-035. Las encuestas de clima son anónimas y no deben mostrarse por empleado.
        if (!cancelled) {
          setHistoryData(events);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error al cargar historial');
          setHistoryData([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, employeeId]);

  const trend = getTrendSummary(historyData);

  /** Datos para el gráfico: ordenados por fecha ascendente */
  const chartData = [...historyData]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((e) => ({
      period: e.period,
      date: e.date,
      score: e.score,
      type: e.type,
    }));

  const handleViewReport = (event: HistoryEvent) => {
    if (onViewNom35Report) {
      onViewNom35Report(employeeId, employeeName);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{employeeName}</DialogTitle>
          <DialogDescription>
            Tendencia: {trend.emoji} {trend.label}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {loading ? (
            <div className="flex h-[280px] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
              {error}
            </div>
          ) : historyData.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">
              No hay evaluaciones completadas para este empleado.
            </p>
          ) : (
            <>
              {/* Gráfico de tendencia */}
              <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <ReferenceArea
                      y1={80}
                      y2={100}
                      fill="rgb(34 197 94 / 0.15)"
                      stroke="none"
                    />
                    <ReferenceArea
                      y1={0}
                      y2={60}
                      fill="rgb(239 68 68 / 0.15)"
                      stroke="none"
                    />
                    <ReferenceLine y={80} stroke="rgb(34 197 94)" strokeDasharray="4 4" />
                    <ReferenceLine y={60} stroke="rgb(239 68 68)" strokeDasharray="4 4" />
                    <XAxis
                      dataKey="period"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      formatter={(value: number) => [`${value} pts`, 'Puntaje']}
                      labelFormatter={(_, payload) => {
                        const p = payload[0]?.payload;
                        return p ? `${p.period} - ${p.type}` : '';
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="hsl(var(--primary))"
                      strokeWidth={3}
                      dot={{ r: 5, fill: 'hsl(var(--primary))' }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-green-500/30" />
                    Alto bienestar (&gt;80)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm bg-red-500/30" />
                    Riesgo (&lt;60)
                  </span>
                </div>
              </div>

              {/* Tabla de eventos */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Historial de evaluaciones</h3>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-2.5 text-left font-medium">Fecha</th>
                        <th className="px-4 py-2.5 text-left font-medium">Tipo de Evaluación</th>
                        <th className="px-4 py-2.5 text-left font-medium">Score</th>
                        <th className="px-4 py-2.5 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...historyData]
                        .sort((a, b) => b.date.localeCompare(a.date))
                        .map((event) => (
                          <tr key={event.id} className="border-b last:border-0">
                            <td className="px-4 py-2.5 text-muted-foreground">
                              {new Date(event.date).toLocaleDateString('es-MX', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </td>
                            <td className="px-4 py-2.5 font-medium">
                              {event.type}
                              {event.guideType && (
                                <span className="ml-1 text-muted-foreground">
                                  ({event.guideType === 'NOM35_G1' ? 'Guía I' : event.guideType === 'NOM35_G2' ? 'Guía II' : 'Guía III'})
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5">
                              <span
                                className={
                                  event.score >= 80
                                    ? 'text-green-600 dark:text-green-400'
                                    : event.score < 60
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-amber-600 dark:text-amber-400'
                                }
                              >
                                {event.score}
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewReport(event)}
                              >
                                <FileDown className="mr-1.5 h-3.5 w-3.5" />
                                Ver Reporte
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
